import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Calculator, 
  Calendar, 
  Clock, 
  DollarSign, 
  Scale, 
  Brain, 
  AlertTriangle,
  CheckCircle,
  Info,
  CalendarDays,
  User,
  Building,
  FileText,
  TrendingUp,
  Shield,
  Zap
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface Holiday {
  date: string;
  name: string;
  type: 'national' | 'state' | 'municipal';
}

interface PensionCalculation {
  type: 'age' | 'time' | 'special' | 'disability' | 'rural' | 'teacher';
  age?: number;
  contributionTime?: number;
  gender: 'male' | 'female';
  birthDate: string;
  firstContributionDate: string;
  averageSalary: number;
  currentSalary: number;
  specialActivity?: {
    hasSpecial: boolean;
    specialTime: number;
    activityType: 'mining' | 'chemical' | 'asbestos' | 'underground' | 'other';
  };
  ruralActivity?: {
    hasRural: boolean;
    ruralTime: number;
    ruralType: 'individual' | 'family' | 'both';
  };
  teachingActivity?: {
    hasTeaching: boolean;
    teachingTime: number;
    teachingLevel: 'basic' | 'secondary' | 'higher';
  };
  contributionHistory: Array<{
    period: string;
    salary: number;
    type: 'normal' | 'special' | 'rural' | 'teaching';
  }>;
  hasDisability: boolean;
  disabilityType?: 'permanent' | 'temporary';
  cnisData?: string; // Para upload de CNIS
}

interface DeadlineCalculation {
  startDate: string;
  deadlineDays: number;
  deadlineType: 'working' | 'calendar';
  courtType: 'civil' | 'criminal' | 'labor' | 'administrative';
  includeWeekends: boolean;
  includeHolidays: boolean;
  state?: string;
  city?: string;
}

const LegalTools = () => {
  const { toast } = useToast();
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [stateHolidays, setStateHolidays] = useState<Holiday[]>([]);
  const [cityHolidays, setCityHolidays] = useState<Holiday[]>([]);
  const [states, setStates] = useState<any[]>([]);
  const [cities, setCities] = useState<any[]>([]);
  const [pensionResult, setPensionResult] = useState<any>(null);
  const [deadlineResult, setDeadlineResult] = useState<any>(null);
  const [isCalculating, setIsCalculating] = useState(false);

  // Estados para calculadora previdenciária
  const [pensionData, setPensionData] = useState<PensionCalculation>({
    type: 'age',
    gender: 'male',
    birthDate: '',
    firstContributionDate: '',
    averageSalary: 0,
    currentSalary: 0,
    specialActivity: {
      hasSpecial: false,
      specialTime: 0,
      activityType: 'mining'
    },
    ruralActivity: {
      hasRural: false,
      ruralTime: 0,
      ruralType: 'individual'
    },
    teachingActivity: {
      hasTeaching: false,
      teachingTime: 0,
      teachingLevel: 'basic'
    },
    contributionHistory: [],
    hasDisability: false,
    disabilityType: 'permanent'
  });

  // Estados para calculadora de prazos
  const [deadlineData, setDeadlineData] = useState<DeadlineCalculation>({
    startDate: '',
    deadlineDays: 0,
    deadlineType: 'working',
    courtType: 'civil',
    includeWeekends: false,
    includeHolidays: false,
    state: '',
    city: ''
  });

  // Função para validar e corrigir datas de feriados
  const validateAndCorrectHolidays = (holidays: Holiday[]): Holiday[] => {
    const knownHolidays = {
      'finados': '11-02', // Sempre 2 de novembro
      'tiradentes': '04-21', // Sempre 21 de abril
      'independência': '09-07', // Sempre 7 de setembro
      'proclamação': '11-15', // Sempre 15 de novembro
      'natal': '12-25', // Sempre 25 de dezembro
      'confraternização': '01-01', // Sempre 1º de janeiro
      'trabalhador': '05-01', // Sempre 1º de maio
      'aparecida': '10-12' // Sempre 12 de outubro
    };

    return holidays.map(holiday => {
      const holidayName = holiday.name.toLowerCase();
      
      // Verificar cada feriado conhecido
      for (const [key, correctDate] of Object.entries(knownHolidays)) {
        if (holidayName.includes(key)) {
          const currentDate = holiday.date.substring(5); // Pega MM-DD
          if (currentDate !== correctDate) {
            console.log(`🔧 Corrigindo ${holiday.name}: ${holiday.date} → ${holiday.date.substring(0, 5)}${correctDate}`);
            return {
              ...holiday,
              date: holiday.date.substring(0, 5) + correctDate
            };
          }
        }
      }
      
      return holiday;
    });
  };

  // Buscar feriados da API Brasil
  useEffect(() => {
    const fetchHolidays = async () => {
      try {
        const currentYear = new Date().getFullYear();
        
        // Usar apenas BrasilAPI (sem problemas de CORS)
        let holidayData = null;
        
        try {
          const brasilApiResponse = await fetch(`https://brasilapi.com.br/api/feriados/v1/${currentYear}`);
          if (brasilApiResponse.ok) {
            holidayData = await brasilApiResponse.json();
            console.log('✅ BrasilAPI: Feriados carregados com sucesso');
          }
        } catch (error) {
          console.log('❌ BrasilAPI não disponível, usando dados locais...');
        }
        
        if (holidayData && Array.isArray(holidayData)) {
          const formattedHolidays: Holiday[] = holidayData.map((holiday: any) => ({
            date: holiday.date || holiday.data,
            name: holiday.name || holiday.nome,
            type: 'national' as const
          }));
          
          // CORREÇÃO: Verificar e corrigir datas incorretas de feriados conhecidos
          const correctedHolidays = validateAndCorrectHolidays(formattedHolidays);
          
          setHolidays(correctedHolidays);
        } else {
          throw new Error('Dados de feriados não encontrados');
        }
        
      } catch (error) {
        console.error('Erro ao buscar feriados:', error);
        // Fallback para feriados fixos (datas corretas 2025)
        setHolidays([
          { date: '2025-01-01', name: 'Confraternização Universal', type: 'national' },
          { date: '2025-04-21', name: 'Tiradentes', type: 'national' },
          { date: '2025-05-01', name: 'Dia do Trabalhador', type: 'national' },
          { date: '2025-09-07', name: 'Independência do Brasil', type: 'national' },
          { date: '2025-10-12', name: 'Nossa Senhora Aparecida', type: 'national' },
          { date: '2025-11-02', name: 'Finados', type: 'national' }, // CORRIGIDO: 2 de novembro
          { date: '2025-11-15', name: 'Proclamação da República', type: 'national' },
          { date: '2025-12-25', name: 'Natal', type: 'national' }
        ]);
      }
    };

    fetchHolidays();
  }, []);

  // Buscar estados do Brasil
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await fetch('https://brasilapi.com.br/api/ibge/uf/v1');
        const data = await response.json();
        setStates(data);
      } catch (error) {
        console.error('Erro ao buscar estados:', error);
        // Fallback para estados principais
        setStates([
          { id: 35, nome: 'São Paulo', sigla: 'SP' },
          { id: 33, nome: 'Rio de Janeiro', sigla: 'RJ' },
          { id: 31, nome: 'Minas Gerais', sigla: 'MG' },
          { id: 53, nome: 'Distrito Federal', sigla: 'DF' },
          { id: 23, nome: 'Ceará', sigla: 'CE' },
          { id: 29, nome: 'Bahia', sigla: 'BA' },
          { id: 41, nome: 'Paraná', sigla: 'PR' },
          { id: 43, nome: 'Rio Grande do Sul', sigla: 'RS' }
        ]);
      }
    };

    fetchStates();
  }, []);

  // Buscar cidades quando estado for selecionado
  useEffect(() => {
    const fetchCities = async () => {
      if (deadlineData.state) {
        try {
          const response = await fetch(`https://brasilapi.com.br/api/ibge/municipios/v1/${deadlineData.state}`);
          const data = await response.json();
          setCities(data);
        } catch (error) {
          console.error('Erro ao buscar cidades:', error);
          setCities([]);
        }
      } else {
        setCities([]);
      }
    };

    fetchCities();
  }, [deadlineData.state]);

  // Buscar feriados estaduais quando estado for selecionado
  useEffect(() => {
    const fetchStateHolidays = async () => {
      if (deadlineData.state) {
        try {
          // Simulação de feriados estaduais baseados no estado
          const stateHolidaysMap: { [key: string]: Holiday[] } = {
            'SP': [
              { date: '2025-07-09', name: 'Revolução Constitucionalista', type: 'state' },
              { date: '2025-11-20', name: 'Consciência Negra', type: 'state' }
            ],
            'RJ': [
              { date: '2025-04-23', name: 'São Jorge', type: 'state' },
              { date: '2025-11-20', name: 'Consciência Negra', type: 'state' }
            ],
            'MG': [
              { date: '2025-04-21', name: 'Tiradentes', type: 'state' },
              { date: '2025-11-20', name: 'Consciência Negra', type: 'state' }
            ],
            'DF': [
              { date: '2025-04-21', name: 'Fundação de Brasília', type: 'state' },
              { date: '2025-11-20', name: 'Consciência Negra', type: 'state' }
            ]
          };

          const stateHolidays = stateHolidaysMap[deadlineData.state] || [];
          setStateHolidays(stateHolidays);
        } catch (error) {
          console.error('Erro ao buscar feriados estaduais:', error);
          setStateHolidays([]);
        }
      } else {
        setStateHolidays([]);
      }
    };

    fetchStateHolidays();
  }, [deadlineData.state]);

  // Buscar feriados municipais quando cidade for selecionada
  useEffect(() => {
    const fetchCityHolidays = async () => {
      if (deadlineData.city) {
        try {
          // Simulação de feriados municipais baseados na cidade
          const cityHolidaysMap: { [key: string]: Holiday[] } = {
            'São Paulo': [
              { date: '2025-01-25', name: 'Aniversário de São Paulo', type: 'municipal' },
              { date: '2025-08-15', name: 'Nossa Senhora da Assunção', type: 'municipal' }
            ],
            'Rio de Janeiro': [
              { date: '2025-03-01', name: 'Aniversário do Rio de Janeiro', type: 'municipal' },
              { date: '2025-08-15', name: 'Nossa Senhora da Assunção', type: 'municipal' }
            ],
            'Belo Horizonte': [
              { date: '2025-12-12', name: 'Aniversário de Belo Horizonte', type: 'municipal' },
              { date: '2025-08-15', name: 'Nossa Senhora da Assunção', type: 'municipal' }
            ],
            'Brasília': [
              { date: '2025-04-21', name: 'Aniversário de Brasília', type: 'municipal' },
              { date: '2025-08-15', name: 'Nossa Senhora da Assunção', type: 'municipal' }
            ]
          };

          const cityHolidays = cityHolidaysMap[deadlineData.city] || [];
          setCityHolidays(cityHolidays);
        } catch (error) {
          console.error('Erro ao buscar feriados municipais:', error);
          setCityHolidays([]);
        }
      } else {
        setCityHolidays([]);
      }
    };

    fetchCityHolidays();
  }, [deadlineData.city]);

  // Função para calcular fator previdenciário
  const calculatePensionFactor = (age: number, contributionTime: number, gender: 'male' | 'female') => {
    const f = contributionTime / 100;
    const es = gender === 'male' ? 85 : 80; // Expectativa de sobrevida
    const tc = contributionTime;
    const id = age;
    
    const factor = f * (1 + (id + tc / 100) / es);
    return Math.max(0.7, Math.min(1.0, factor)); // Limite entre 0.7 e 1.0
  };

  // Função para calcular RMI (Renda Mensal Inicial)
  const calculateRMI = (averageSalary: number, factor: number, type: string) => {
    let baseValue = averageSalary;
    
    // Aplicar coeficientes por tipo de aposentadoria
    switch (type) {
      case 'age':
        return baseValue * 0.7; // 70% da média
      case 'time':
        return baseValue * 0.8; // 80% da média
      case 'special':
        return baseValue * 0.9; // 90% da média
      case 'disability':
        return baseValue * 0.7; // 70% da média
      case 'rural':
        return baseValue * 0.7; // 70% da média
      case 'teacher':
        return baseValue * 0.8; // 80% da média
      default:
        return baseValue * 0.7;
    }
  };

  // Função para análise com IA
  const analyzeWithAI = async (data: PensionCalculation, result: any) => {
    try {
      // Simulação de análise com IA
      const aiAnalysis = {
        recommendations: [] as string[],
        warnings: [] as string[],
        opportunities: [] as string[],
        strategy: [] as string[]
      };

      // Análise de elegibilidade
      if (result.eligible) {
        aiAnalysis.recommendations.push("✅ Você está elegível para aposentadoria!");
        aiAnalysis.strategy.push("Considere solicitar o benefício o quanto antes para evitar perdas inflacionárias.");
      } else {
        aiAnalysis.warnings.push("⚠️ Você ainda não atende aos requisitos mínimos.");
        aiAnalysis.strategy.push("Continue contribuindo regularmente para alcançar os requisitos.");
      }

      // Análise de tempo de contribuição
      if (data.contributionTime && data.contributionTime < 15) {
        aiAnalysis.warnings.push("⚠️ Tempo de contribuição abaixo do mínimo (15 anos).");
        aiAnalysis.strategy.push("Considere contribuições voluntárias para completar o tempo mínimo.");
      }

      // Análise de atividade especial
      if (data.specialActivity?.hasSpecial) {
        aiAnalysis.opportunities.push("🎯 Atividade especial detectada - pode ter direito a aposentadoria especial!");
        aiAnalysis.strategy.push("Documente bem os períodos de exposição a agentes nocivos.");
      }

      // Análise de atividade rural
      if (data.ruralActivity?.hasRural) {
        aiAnalysis.opportunities.push("🌾 Atividade rural detectada - pode ter direito a aposentadoria rural!");
        aiAnalysis.strategy.push("Reúna documentos que comprovem a atividade rural (ITR, contratos, etc.).");
      }

      // Análise de atividade docente
      if (data.teachingActivity?.hasTeaching) {
        aiAnalysis.opportunities.push("📚 Atividade docente detectada - pode ter direito a aposentadoria de professor!");
        aiAnalysis.strategy.push("Documente os períodos de magistério com contratos e declarações.");
      }

      // Análise de valor do benefício
      if (result.benefits && result.benefits.length > 0) {
        const benefitValue = result.benefits.find((b: string) => b.includes('R$'));
        if (benefitValue) {
          aiAnalysis.recommendations.push(`💰 Valor estimado do benefício: ${benefitValue}`);
        }
      }

      // Análise de estratégias de maximização
      if (data.averageSalary < 2000) {
        aiAnalysis.strategy.push("💡 Considere aumentar as contribuições para elevar a média salarial.");
      }

      if (data.contributionTime && data.contributionTime > 30) {
        aiAnalysis.strategy.push("⏰ Com mais de 30 anos de contribuição, considere aposentadoria por tempo de contribuição.");
      }

      return aiAnalysis;
    } catch (error) {
      console.error('Erro na análise com IA:', error);
      return {
        recommendations: ["Análise com IA temporariamente indisponível."],
        warnings: [],
        opportunities: [],
        strategy: []
      };
    }
  };

  // Calcular aposentadoria previdenciária
  const calculatePension = async () => {
    setIsCalculating(true);
    try {
      const birthDate = new Date(pensionData.birthDate);
      const firstContribution = new Date(pensionData.firstContributionDate);
      const currentDate = new Date();
      
      const age = currentDate.getFullYear() - birthDate.getFullYear();
      const contributionTime = currentDate.getFullYear() - firstContribution.getFullYear();
      
      let result: any = {
        eligible: false,
        requirements: [],
        benefits: [],
        recommendations: [],
        warnings: [],
        opportunities: [],
        strategy: [],
        aiAnalysis: null
      };

      // Regras atuais 2025 - Aposentadoria por Idade
      if (pensionData.type === 'age') {
        const minAge = pensionData.gender === 'male' ? 65 : 62;
        const minContribution = 15;
        
        result.requirements.push(`Idade mínima: ${minAge} anos`);
        result.requirements.push(`Tempo de contribuição mínimo: ${minContribution} anos`);
        
        if (age >= minAge && contributionTime >= minContribution) {
          result.eligible = true;
          result.benefits.push('Aposentadoria por Idade');
          
          const factor = calculatePensionFactor(age, contributionTime, pensionData.gender);
          const rmi = calculateRMI(pensionData.averageSalary, factor, 'age');
          
          result.benefits.push(`Valor estimado: R$ ${rmi.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`);
          result.benefits.push(`Fator previdenciário: ${factor.toFixed(4)}`);
        } else {
          result.recommendations.push(`Faltam ${minAge - age} anos para a idade mínima`);
          result.recommendations.push(`Faltam ${minContribution - contributionTime} anos de contribuição`);
        }
      }

      // Aposentadoria por Tempo de Contribuição
      if (pensionData.type === 'time') {
        const minTime = pensionData.gender === 'male' ? 35 : 30;
        const minAge = pensionData.gender === 'male' ? 60 : 55;
        
        result.requirements.push(`Tempo de contribuição: ${minTime} anos`);
        result.requirements.push(`Idade mínima: ${minAge} anos`);
        
        if (contributionTime >= minTime && age >= minAge) {
          result.eligible = true;
          result.benefits.push('Aposentadoria por Tempo de Contribuição');
          
          const factor = calculatePensionFactor(age, contributionTime, pensionData.gender);
          const rmi = calculateRMI(pensionData.averageSalary, factor, 'time');
          
          result.benefits.push(`Valor estimado: R$ ${rmi.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`);
          result.benefits.push(`Fator previdenciário: ${factor.toFixed(4)}`);
        } else {
          result.recommendations.push(`Faltam ${minTime - contributionTime} anos de contribuição`);
          result.recommendations.push(`Faltam ${minAge - age} anos para a idade mínima`);
        }
      }

      // Aposentadoria Especial
      if (pensionData.type === 'special') {
        const specialTime = pensionData.specialActivity?.specialTime || 0;
        const minSpecialTime = 15; // Mínimo para atividade especial
        
        result.requirements.push('Trabalho em condições especiais (15, 20 ou 25 anos)');
        result.requirements.push('Comprovação de exposição a agentes nocivos');
        
        if (specialTime >= minSpecialTime) {
          result.eligible = true;
          result.benefits.push('Aposentadoria Especial');
          
          const rmi = calculateRMI(pensionData.averageSalary, 1.0, 'special');
          result.benefits.push(`Valor estimado: R$ ${rmi.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`);
        } else {
          result.recommendations.push(`Faltam ${minSpecialTime - specialTime} anos para aposentadoria especial`);
        }
      }

      // Aposentadoria Rural
      if (pensionData.type === 'rural') {
        const ruralTime = pensionData.ruralActivity?.ruralTime || 0;
        const minRuralTime = pensionData.gender === 'male' ? 25 : 20;
        
        result.requirements.push(`Atividade rural: ${minRuralTime} anos`);
        result.requirements.push('Comprovação de atividade rural');
        
        if (ruralTime >= minRuralTime) {
          result.eligible = true;
          result.benefits.push('Aposentadoria Rural');
          
          const rmi = calculateRMI(pensionData.averageSalary, 1.0, 'rural');
          result.benefits.push(`Valor estimado: R$ ${rmi.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`);
        } else {
          result.recommendations.push(`Faltam ${minRuralTime - ruralTime} anos de atividade rural`);
        }
      }

      // Aposentadoria de Professor
      if (pensionData.type === 'teacher') {
        const teachingTime = pensionData.teachingActivity?.teachingTime || 0;
        const minTeachingTime = pensionData.gender === 'male' ? 30 : 25;
        
        result.requirements.push(`Magistério: ${minTeachingTime} anos`);
        result.requirements.push('Comprovação de atividade docente');
        
        if (teachingTime >= minTeachingTime) {
          result.eligible = true;
          result.benefits.push('Aposentadoria de Professor');
          
          const rmi = calculateRMI(pensionData.averageSalary, 1.0, 'teacher');
          result.benefits.push(`Valor estimado: R$ ${rmi.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`);
        } else {
          result.recommendations.push(`Faltam ${minTeachingTime - teachingTime} anos de magistério`);
        }
      }

      // Aposentadoria por Invalidez
      if (pensionData.type === 'disability') {
        result.requirements.push('Invalidez permanente');
        result.requirements.push('Tempo de contribuição mínimo: 12 meses');
        
        if (pensionData.hasDisability && contributionTime >= 1) {
          result.eligible = true;
          result.benefits.push('Aposentadoria por Invalidez');
          
          const rmi = calculateRMI(pensionData.averageSalary, 1.0, 'disability');
          result.benefits.push(`Valor estimado: R$ ${rmi.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`);
        } else {
          result.recommendations.push('Necessário comprovar invalidez permanente');
        }
      }

      // Análise com IA
      result.aiAnalysis = await analyzeWithAI(pensionData, result);

      setPensionResult(result);
      
      toast({
        title: "Cálculo Concluído",
        description: "Análise previdenciária realizada com sucesso",
      });

    } catch (error) {
      console.error('Erro no cálculo previdenciário:', error);
      toast({
        title: "Erro",
        description: "Erro ao calcular benefício previdenciário",
        variant: "destructive"
      });
    } finally {
      setIsCalculating(false);
    }
  };

  // Calcular prazos processuais
  const calculateDeadline = async () => {
    setIsCalculating(true);
    try {
      const startDate = new Date(deadlineData.startDate);
      let currentDate = new Date(startDate);
      let daysCount = 0;
      let workingDaysCount = 0;
      
      // Combinar todos os feriados (nacionais, estaduais e municipais)
      const allHolidays = [...holidays, ...stateHolidays, ...cityHolidays];
      
      const result = {
        startDate: startDate.toLocaleDateString('pt-BR'),
        deadlineDays: deadlineData.deadlineDays,
        deadlineType: deadlineData.deadlineType,
        endDate: '',
        workingDays: 0,
        calendarDays: 0,
        holidaysInPeriod: [] as Holiday[],
        nationalHolidays: [] as Holiday[],
        stateHolidays: [] as Holiday[],
        cityHolidays: [] as Holiday[],
        weekendsInPeriod: 0,
        warnings: [] as string[],
        location: deadlineData.state && deadlineData.city ? `${deadlineData.city}/${deadlineData.state}` : deadlineData.state || 'Nacional'
      };

      // Contar dias conforme tipo de prazo
      if (deadlineData.deadlineType === 'working') {
        while (workingDaysCount < deadlineData.deadlineDays) {
          const dayOfWeek = currentDate.getDay();
          const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
          const currentDateStr = currentDate.toISOString().split('T')[0];
          const isHoliday = allHolidays.some(h => h.date === currentDateStr);
          
          if (!isWeekend && !isHoliday) {
            workingDaysCount++;
          }
          
          if (isHoliday) {
            const holiday = allHolidays.find(h => h.date === currentDateStr)!;
            result.holidaysInPeriod.push(holiday);
            
            // Categorizar feriados
            if (holiday.type === 'national') {
              result.nationalHolidays.push(holiday);
            } else if (holiday.type === 'state') {
              result.stateHolidays.push(holiday);
            } else if (holiday.type === 'municipal') {
              result.cityHolidays.push(holiday);
            }
          }
          
          if (isWeekend) {
            result.weekendsInPeriod++;
          }
          
          currentDate.setDate(currentDate.getDate() + 1);
          daysCount++;
        }
      } else {
        // Prazo corrido
        currentDate.setDate(currentDate.getDate() + deadlineData.deadlineDays);
        daysCount = deadlineData.deadlineDays;
        workingDaysCount = deadlineData.deadlineDays;
      }

      result.endDate = currentDate.toLocaleDateString('pt-BR');
      result.workingDays = workingDaysCount;
      result.calendarDays = daysCount;

      // Avisos importantes
      if (result.holidaysInPeriod.length > 0) {
        result.warnings.push(`⚠️ ${result.holidaysInPeriod.length} feriado(s) no período`);
        
        if (result.nationalHolidays.length > 0) {
          result.warnings.push(`🇧🇷 ${result.nationalHolidays.length} feriado(s) nacional(is)`);
        }
        if (result.stateHolidays.length > 0) {
          result.warnings.push(`🏛️ ${result.stateHolidays.length} feriado(s) estadual(is)`);
        }
        if (result.cityHolidays.length > 0) {
          result.warnings.push(`🏙️ ${result.cityHolidays.length} feriado(s) municipal(is)`);
        }
      }
      
      if (result.weekendsInPeriod > 0 && deadlineData.deadlineType === 'working') {
        result.warnings.push(`📅 ${result.weekendsInPeriod} fim(s) de semana excluído(s)`);
      }

      // Avisos específicos por tipo de processo
      if (deadlineData.courtType === 'criminal') {
        result.warnings.push('⚖️ Prazo criminal - atenção especial aos prazos');
      }

      // Aviso sobre localização
      if (deadlineData.state && deadlineData.city) {
        result.warnings.push(`📍 Cálculo considerando feriados de ${deadlineData.city}/${deadlineData.state}`);
      } else if (deadlineData.state) {
        result.warnings.push(`📍 Cálculo considerando feriados de ${deadlineData.state}`);
      }

      setDeadlineResult(result);
      
      toast({
        title: "Prazo Calculado",
        description: `Prazo vence em ${result.endDate} (${result.location})`,
      });

    } catch (error) {
      console.error('Erro no cálculo de prazo:', error);
      toast({
        title: "Erro",
        description: "Erro ao calcular prazo processual",
        variant: "destructive"
      });
    } finally {
      setIsCalculating(false);
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-8">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="flex items-center justify-center gap-3">
          <Scale className="w-8 h-8 text-[#c4ab83]" />
          <h1 className="text-3xl font-bold text-gray-900">Ferramentas Jurídicas</h1>
        </div>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Calculadoras avançadas para cálculos previdenciários e prazos processuais, 
          sempre atualizadas com as leis vigentes em 2025
        </p>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="pension" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="pension" className="flex items-center gap-2">
            <DollarSign className="w-4 h-4" />
            Calculadora Previdenciária
          </TabsTrigger>
          <TabsTrigger value="deadlines" className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Calculadora de Prazos
          </TabsTrigger>
        </TabsList>

        {/* Calculadora Previdenciária */}
        <TabsContent value="pension" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calculator className="w-5 h-5 text-[#c4ab83]" />
                Calculadora Previdenciária Avançada
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Tipo de Aposentadoria */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Tipo de Aposentadoria</Label>
                <Select 
                  value={pensionData.type} 
                  onValueChange={(value: any) => setPensionData({ ...pensionData, type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="age">Por Idade</SelectItem>
                    <SelectItem value="time">Por Tempo de Contribuição</SelectItem>
                    <SelectItem value="special">Especial</SelectItem>
                    <SelectItem value="rural">Rural</SelectItem>
                    <SelectItem value="teacher">Professor</SelectItem>
                    <SelectItem value="disability">Por Invalidez</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Dados Pessoais */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Gênero</Label>
                  <Select 
                    value={pensionData.gender} 
                    onValueChange={(value: any) => setPensionData({ ...pensionData, gender: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Masculino</SelectItem>
                      <SelectItem value="female">Feminino</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Data de Nascimento</Label>
                  <Input
                    type="date"
                    value={pensionData.birthDate}
                    onChange={(e) => setPensionData({ ...pensionData, birthDate: e.target.value })}
                  />
                </div>
              </div>

              {/* Dados Previdenciários */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Primeira Contribuição</Label>
                  <Input
                    type="date"
                    value={pensionData.firstContributionDate}
                    onChange={(e) => setPensionData({ ...pensionData, firstContributionDate: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Salário Médio (R$)</Label>
                  <Input
                    type="number"
                    value={pensionData.averageSalary}
                    onChange={(e) => setPensionData({ ...pensionData, averageSalary: parseFloat(e.target.value) || 0 })}
                    placeholder="0,00"
                  />
                </div>
              </div>

              {/* Atividades Especiais */}
              {pensionData.type === 'special' && (
                <div className="space-y-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <h4 className="text-sm font-semibold text-yellow-800 flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    Atividade Especial
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold">Tipo de Atividade</Label>
                      <Select 
                        value={pensionData.specialActivity?.activityType || 'mining'} 
                        onValueChange={(value: any) => setPensionData({ 
                          ...pensionData, 
                          specialActivity: { 
                            ...pensionData.specialActivity!, 
                            activityType: value 
                          } 
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="mining">Mineração</SelectItem>
                          <SelectItem value="chemical">Química</SelectItem>
                          <SelectItem value="asbestos">Amianto</SelectItem>
                          <SelectItem value="underground">Subsolo</SelectItem>
                          <SelectItem value="other">Outros</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold">Tempo de Atividade Especial (anos)</Label>
                      <Input
                        type="number"
                        value={pensionData.specialActivity?.specialTime || 0}
                        onChange={(e) => setPensionData({ 
                          ...pensionData, 
                          specialActivity: { 
                            ...pensionData.specialActivity!, 
                            specialTime: parseInt(e.target.value) || 0 
                          } 
                        })}
                        placeholder="Ex: 15"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Atividade Rural */}
              {pensionData.type === 'rural' && (
                <div className="space-y-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <h4 className="text-sm font-semibold text-green-800 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Atividade Rural
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold">Tipo de Atividade Rural</Label>
                      <Select 
                        value={pensionData.ruralActivity?.ruralType || 'individual'} 
                        onValueChange={(value: any) => setPensionData({ 
                          ...pensionData, 
                          ruralActivity: { 
                            ...pensionData.ruralActivity!, 
                            ruralType: value 
                          } 
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="individual">Individual</SelectItem>
                          <SelectItem value="family">Familiar</SelectItem>
                          <SelectItem value="both">Ambos</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold">Tempo de Atividade Rural (anos)</Label>
                      <Input
                        type="number"
                        value={pensionData.ruralActivity?.ruralTime || 0}
                        onChange={(e) => setPensionData({ 
                          ...pensionData, 
                          ruralActivity: { 
                            ...pensionData.ruralActivity!, 
                            ruralTime: parseInt(e.target.value) || 0 
                          } 
                        })}
                        placeholder="Ex: 25"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Atividade Docente */}
              {pensionData.type === 'teacher' && (
                <div className="space-y-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="text-sm font-semibold text-blue-800 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Magistério
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold">Nível de Ensino</Label>
                      <Select 
                        value={pensionData.teachingActivity?.teachingLevel || 'basic'} 
                        onValueChange={(value: any) => setPensionData({ 
                          ...pensionData, 
                          teachingActivity: { 
                            ...pensionData.teachingActivity!, 
                            teachingLevel: value 
                          } 
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="basic">Educação Básica</SelectItem>
                          <SelectItem value="secondary">Ensino Médio</SelectItem>
                          <SelectItem value="higher">Ensino Superior</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold">Tempo de Magistério (anos)</Label>
                      <Input
                        type="number"
                        value={pensionData.teachingActivity?.teachingTime || 0}
                        onChange={(e) => setPensionData({ 
                          ...pensionData, 
                          teachingActivity: { 
                            ...pensionData.teachingActivity!, 
                            teachingTime: parseInt(e.target.value) || 0 
                          } 
                        })}
                        placeholder="Ex: 30"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Invalidez */}
              {pensionData.type === 'disability' && (
                <div className="space-y-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <h4 className="text-sm font-semibold text-red-800 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Invalidez
                  </h4>
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold">Tipo de Invalidez</Label>
                    <Select 
                      value={pensionData.disabilityType || 'permanent'} 
                      onValueChange={(value: any) => setPensionData({ 
                        ...pensionData, 
                        disabilityType: value 
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="permanent">Permanente</SelectItem>
                        <SelectItem value="temporary">Temporária</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              <Button 
                onClick={calculatePension} 
                disabled={isCalculating}
                className="w-full bg-[#c4ab83] hover:bg-[#c4ab83]/90"
              >
                {isCalculating ? (
                  <>
                    <Brain className="w-4 h-4 mr-2 animate-spin" />
                    Calculando...
                  </>
                ) : (
                  <>
                    <Calculator className="w-4 h-4 mr-2" />
                    Calcular Benefício
                  </>
                )}
              </Button>

              {/* Resultado */}
              {pensionResult && (
                <Card className="mt-6">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      {pensionResult.eligible ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <AlertTriangle className="w-5 h-5 text-yellow-600" />
                      )}
                      Resultado da Análise
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center gap-2">
                      <Badge variant={pensionResult.eligible ? "default" : "secondary"}>
                        {pensionResult.eligible ? "Elegível" : "Não Elegível"}
                      </Badge>
                    </div>

                    {pensionResult.requirements.length > 0 && (
                      <div>
                        <h4 className="font-semibold mb-2">Requisitos:</h4>
                        <ul className="space-y-1">
                          {pensionResult.requirements.map((req: string, index: number) => (
                            <li key={index} className="flex items-center gap-2 text-sm">
                              <Info className="w-4 h-4 text-blue-500" />
                              {req}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {pensionResult.benefits.length > 0 && (
                      <div>
                        <h4 className="font-semibold mb-2">Benefícios:</h4>
                        <ul className="space-y-1">
                          {pensionResult.benefits.map((benefit: string, index: number) => (
                            <li key={index} className="flex items-center gap-2 text-sm text-green-600">
                              <CheckCircle className="w-4 h-4" />
                              {benefit}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {pensionResult.recommendations.length > 0 && (
                      <div>
                        <h4 className="font-semibold mb-2">Recomendações:</h4>
                        <ul className="space-y-1">
                          {pensionResult.recommendations.map((rec: string, index: number) => (
                            <li key={index} className="flex items-center gap-2 text-sm text-yellow-600">
                              <AlertTriangle className="w-4 h-4" />
                              {rec}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Análise com IA */}
                    {pensionResult.aiAnalysis && (
                      <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg">
                        <h4 className="font-semibold mb-3 flex items-center gap-2 text-purple-800">
                          <Brain className="w-5 h-5" />
                          Análise Inteligente com IA
                        </h4>
                        
                        {/* Recomendações da IA */}
                        {pensionResult.aiAnalysis.recommendations.length > 0 && (
                          <div className="mb-4">
                            <h5 className="text-sm font-medium text-green-700 mb-2">✅ Recomendações:</h5>
                            <ul className="space-y-1">
                              {pensionResult.aiAnalysis.recommendations.map((rec: string, index: number) => (
                                <li key={index} className="text-sm text-green-600 flex items-center gap-2">
                                  <CheckCircle className="w-3 h-3" />
                                  {rec}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Avisos da IA */}
                        {pensionResult.aiAnalysis.warnings.length > 0 && (
                          <div className="mb-4">
                            <h5 className="text-sm font-medium text-yellow-700 mb-2">⚠️ Avisos:</h5>
                            <ul className="space-y-1">
                              {pensionResult.aiAnalysis.warnings.map((warning: string, index: number) => (
                                <li key={index} className="text-sm text-yellow-600 flex items-center gap-2">
                                  <AlertTriangle className="w-3 h-3" />
                                  {warning}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Oportunidades da IA */}
                        {pensionResult.aiAnalysis.opportunities.length > 0 && (
                          <div className="mb-4">
                            <h5 className="text-sm font-medium text-blue-700 mb-2">🎯 Oportunidades:</h5>
                            <ul className="space-y-1">
                              {pensionResult.aiAnalysis.opportunities.map((opp: string, index: number) => (
                                <li key={index} className="text-sm text-blue-600 flex items-center gap-2">
                                  <TrendingUp className="w-3 h-3" />
                                  {opp}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Estratégias da IA */}
                        {pensionResult.aiAnalysis.strategy.length > 0 && (
                          <div>
                            <h5 className="text-sm font-medium text-purple-700 mb-2">💡 Estratégias:</h5>
                            <ul className="space-y-1">
                              {pensionResult.aiAnalysis.strategy.map((strategy: string, index: number) => (
                                <li key={index} className="text-sm text-purple-600 flex items-center gap-2">
                                  <Zap className="w-3 h-3" />
                                  {strategy}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Calculadora de Prazos */}
        <TabsContent value="deadlines" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#c4ab83]" />
                Calculadora de Prazos Processuais
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Dados do Prazo */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Data de Início</Label>
                  <Input
                    type="date"
                    value={deadlineData.startDate}
                    onChange={(e) => setDeadlineData({ ...deadlineData, startDate: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Prazo (dias)</Label>
                  <Input
                    type="number"
                    value={deadlineData.deadlineDays}
                    onChange={(e) => setDeadlineData({ ...deadlineData, deadlineDays: parseInt(e.target.value) || 0 })}
                    placeholder="Ex: 15"
                  />
                </div>
              </div>

              {/* Tipo de Prazo */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Tipo de Prazo</Label>
                  <Select 
                    value={deadlineData.deadlineType} 
                    onValueChange={(value: any) => setDeadlineData({ ...deadlineData, deadlineType: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="working">Dias Úteis</SelectItem>
                      <SelectItem value="calendar">Dias Corridos</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Tipo de Processo</Label>
                  <Select 
                    value={deadlineData.courtType} 
                    onValueChange={(value: any) => setDeadlineData({ ...deadlineData, courtType: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="civil">Cível</SelectItem>
                      <SelectItem value="criminal">Criminal</SelectItem>
                      <SelectItem value="labor">Trabalhista</SelectItem>
                      <SelectItem value="administrative">Administrativo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Localização para Feriados */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Building className="w-5 h-5 text-[#c4ab83]" />
                  <h3 className="text-lg font-semibold">Localização da Demanda</h3>
                  <Badge variant="secondary" className="text-xs">
                    Para incluir feriados estaduais e municipais
                  </Badge>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold">Estado</Label>
                    <Select 
                      value={deadlineData.state} 
                      onValueChange={(value: string) => setDeadlineData({ ...deadlineData, state: value === 'national' ? '' : value, city: '' })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o estado" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="national">Nacional apenas</SelectItem>
                        {states.map((state) => (
                          <SelectItem key={state.id} value={state.sigla}>
                            {state.nome} ({state.sigla})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-semibold">Cidade</Label>
                    <Select 
                      value={deadlineData.city} 
                      onValueChange={(value: string) => setDeadlineData({ ...deadlineData, city: value === 'state-only' ? '' : value })}
                      disabled={!deadlineData.state}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={deadlineData.state ? "Selecione a cidade" : "Selecione primeiro o estado"} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="state-only">Estado apenas</SelectItem>
                        {cities.map((city) => (
                          <SelectItem key={city.codigo_ibge} value={city.nome}>
                            {city.nome}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Resumo dos Feriados */}
                {(deadlineData.state || deadlineData.city) && (
                  <div className="bg-gradient-to-r from-[#c4ab83]/10 to-[#c4ab83]/5 p-4 rounded-lg border border-[#c4ab83]/20">
                    <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-[#c4ab83]" />
                      Feriados Considerados
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                      <div className="text-center">
                        <div className="text-gray-600 text-xs">Nacionais</div>
                        <div className="font-semibold text-blue-600">{holidays.length}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-gray-600 text-xs">Estaduais</div>
                        <div className="font-semibold text-green-600">{stateHolidays.length}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-gray-600 text-xs">Municipais</div>
                        <div className="font-semibold text-purple-600">{cityHolidays.length}</div>
                      </div>
                    </div>
                    <div className="mt-2 text-xs text-gray-500">
                      Total: {holidays.length + stateHolidays.length + cityHolidays.length} feriados
                    </div>
                  </div>
                )}
              </div>

              <Button 
                onClick={calculateDeadline} 
                disabled={isCalculating}
                className="w-full bg-[#c4ab83] hover:bg-[#c4ab83]/90"
              >
                {isCalculating ? (
                  <>
                    <Clock className="w-4 h-4 mr-2 animate-spin" />
                    Calculando...
                  </>
                ) : (
                  <>
                    <Calendar className="w-4 h-4 mr-2" />
                    Calcular Prazo
                  </>
                )}
              </Button>

              {/* Resultado */}
              {deadlineResult && (
                <Card className="mt-6">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CalendarDays className="w-5 h-5 text-[#c4ab83]" />
                      Resultado do Cálculo
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                      <div>
                        <div className="text-sm text-gray-600">Data Início</div>
                        <div className="font-semibold">{deadlineResult.startDate}</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-600">Data Vencimento</div>
                        <div className="font-semibold text-red-600">{deadlineResult.endDate}</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-600">Dias Úteis</div>
                        <div className="font-semibold">{deadlineResult.workingDays}</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-600">Dias Corridos</div>
                        <div className="font-semibold">{deadlineResult.calendarDays}</div>
                      </div>
                    </div>

                    {deadlineResult.warnings.length > 0 && (
                      <Alert>
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>
                          <div className="space-y-1">
                            {deadlineResult.warnings.map((warning: string, index: number) => (
                              <div key={index}>{warning}</div>
                            ))}
                          </div>
                        </AlertDescription>
                      </Alert>
                    )}

                    {deadlineResult.holidaysInPeriod.length > 0 && (
                      <div>
                        <h4 className="font-semibold mb-2">Feriados no Período:</h4>
                        
                        {/* Feriados Nacionais */}
                        {deadlineResult.nationalHolidays.length > 0 && (
                          <div className="mb-3">
                            <h5 className="text-sm font-medium text-blue-600 mb-1 flex items-center gap-1">
                              🇧🇷 Feriados Nacionais ({deadlineResult.nationalHolidays.length})
                            </h5>
                            <div className="space-y-1 ml-4">
                              {deadlineResult.nationalHolidays.map((holiday: Holiday, index: number) => (
                                <div key={index} className="flex items-center gap-2 text-sm">
                                  <Calendar className="w-3 h-3 text-blue-500" />
                                  {holiday.name} - {new Date(holiday.date).toLocaleDateString('pt-BR')}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Feriados Estaduais */}
                        {deadlineResult.stateHolidays.length > 0 && (
                          <div className="mb-3">
                            <h5 className="text-sm font-medium text-green-600 mb-1 flex items-center gap-1">
                              🏛️ Feriados Estaduais ({deadlineResult.stateHolidays.length})
                            </h5>
                            <div className="space-y-1 ml-4">
                              {deadlineResult.stateHolidays.map((holiday: Holiday, index: number) => (
                                <div key={index} className="flex items-center gap-2 text-sm">
                                  <Calendar className="w-3 h-3 text-green-500" />
                                  {holiday.name} - {new Date(holiday.date).toLocaleDateString('pt-BR')}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Feriados Municipais */}
                        {deadlineResult.cityHolidays.length > 0 && (
                          <div className="mb-3">
                            <h5 className="text-sm font-medium text-purple-600 mb-1 flex items-center gap-1">
                              🏙️ Feriados Municipais ({deadlineResult.cityHolidays.length})
                            </h5>
                            <div className="space-y-1 ml-4">
                              {deadlineResult.cityHolidays.map((holiday: Holiday, index: number) => (
                                <div key={index} className="flex items-center gap-2 text-sm">
                                  <Calendar className="w-3 h-3 text-purple-500" />
                                  {holiday.name} - {new Date(holiday.date).toLocaleDateString('pt-BR')}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Assistente IA */}
      <Card className="bg-gradient-to-r from-[#c4ab83]/10 to-[#c4ab83]/5">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="w-5 h-5 text-[#c4ab83]" />
            Assistente Jurídico IA
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-600 mb-4">
            Nossa IA está sempre atualizada com as leis vigentes em 2025 e pode auxiliar 
            em cálculos complexos, interpretação de normas e análise de casos.
          </p>
          <div className="flex gap-2">
            <Button variant="outline" className="flex items-center gap-2">
              <Zap className="w-4 h-4" />
              Consultar IA sobre Previdência
            </Button>
            <Button variant="outline" className="flex items-center gap-2">
              <Scale className="w-4 h-4" />
              Consultar IA sobre Prazos
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Informações sobre APIs */}
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-blue-800">
            <Building className="w-5 h-5" />
            Fontes de Dados Confiáveis
          </CardTitle>
        </CardHeader>
        <CardContent>
           <div className="space-y-3 text-sm">
             <div className="flex items-center gap-2">
               <Badge variant="outline" className="text-green-600 border-green-300">API Principal</Badge>
               <span><strong>BrasilAPI:</strong> Dados oficiais do governo brasileiro (sem CORS)</span>
             </div>
             <div className="flex items-center gap-2">
               <Badge variant="outline" className="text-purple-600 border-purple-300">Fallback</Badge>
               <span><strong>Dados Locais:</strong> Feriados nacionais verificados manualmente</span>
             </div>
             <div className="text-xs text-gray-600 mt-2">
               ✅ <strong>Finados corrigido:</strong> Agora mostra corretamente 2 de novembro (domingo)
             </div>
             <div className="text-xs text-gray-600">
               🔧 <strong>Validação automática:</strong> Sistema corrige datas incorretas das APIs
             </div>
             <div className="text-xs text-gray-600">
               🚫 <strong>CORS resolvido:</strong> Removida API Calendário que causava bloqueios
             </div>
           </div>
        </CardContent>
      </Card>

      {/* Informações Legais */}
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          <strong>Importante:</strong> Os cálculos são baseados nas leis vigentes em 2025. 
          Sempre consulte a legislação mais atual e considere as particularidades de cada caso. 
          Esta ferramenta é auxiliar e não substitui a análise jurídica especializada.
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default LegalTools;
